import { useMutation, useQuery, useQueryClient, } from "@tanstack/react-query";
import { CACHE_FRESH_TIME, CACHE_RETENTION_TIME } from "./constants";
const areDriverParamsValid = (driverName, driverSubType) => {
    const isDriverNameValid = driverName === undefined || Boolean(driverName);
    const isDriverSubTypeValid = driverSubType === undefined || Boolean(driverSubType);
    return isDriverNameValid && isDriverSubTypeValid;
};
function useForecastQuery(options) {
    return useQuery({
        staleTime: CACHE_FRESH_TIME,
        gcTime: CACHE_RETENTION_TIME,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
        ...options,
    });
}
const getDayForecastQueryKey = (params) => {
    const { unitId, date, driverName, driverSubType } = params;
    return ["day-forecast", unitId, date, driverName, driverSubType];
};
const getDayForecastQueryFn = (fetchApi, params, signal) => {
    return async () => {
        const { unitId, date, driverName, driverSubType } = params;
        const queryParams = new URLSearchParams();
        queryParams.append("date", date);
        if (driverName !== undefined && driverName !== "") {
            queryParams.append("driverName", driverName);
        }
        if (driverSubType !== undefined && driverSubType !== "") {
            queryParams.append("driverSubType", driverSubType);
        }
        try {
            const res = await fetchApi.jsonInJsonOut(`/forecast/unit/${unitId}/day?${queryParams.toString()}`, {
                signal,
            });
            return res?.payload;
        }
        catch (error) {
            console.error("Error fetching day forecast: %o", error);
            return undefined;
        }
    };
};
export const prefetchDayForecast = (queryClient, fetchApi, params) => {
    const queryKey = getDayForecastQueryKey(params);
    const queryFn = getDayForecastQueryFn(fetchApi, params);
    void queryClient.prefetchQuery({
        queryKey,
        queryFn,
        staleTime: CACHE_FRESH_TIME,
        gcTime: CACHE_RETENTION_TIME,
    });
};
export const useDayForecast = (fetchApi, params, signal, enabled = true) => {
    const { driverName, driverSubType } = params;
    const queryKey = getDayForecastQueryKey(params);
    const { data, refetch, isLoading } = useForecastQuery({
        queryKey,
        enabled: enabled && areDriverParamsValid(driverName, driverSubType),
        queryFn: getDayForecastQueryFn(fetchApi, params, signal),
    });
    return {
        data,
        isLoading,
        refetch,
    };
};
const getWeekForecastQueryKey = (params) => {
    const { unitId, weekStartDate, driverName, driverSubType } = params;
    return [
        "week-forecast",
        unitId,
        weekStartDate,
        driverName,
        driverSubType,
    ];
};
const getWeekForecastQueryFn = (fetchApi, params, signal) => {
    return async () => {
        const { unitId, weekStartDate, driverName, driverSubType } = params;
        const queryParams = new URLSearchParams();
        queryParams.append("weekStartDate", weekStartDate);
        if (driverName !== undefined && driverName !== "") {
            queryParams.append("driverName", driverName);
        }
        if (driverSubType !== undefined && driverSubType !== "") {
            queryParams.append("driverSubType", driverSubType);
        }
        try {
            const res = await fetchApi.jsonInJsonOut(`/forecast/unit/${unitId}/week?${queryParams.toString()}`, {
                signal,
            });
            return res?.payload;
        }
        catch (error) {
            console.error("Error fetching week forecast: %o", error);
            return undefined;
        }
    };
};
export const prefetchWeekForecast = (queryClient, fetchApi, params) => {
    const queryKey = getWeekForecastQueryKey(params);
    const queryFn = getWeekForecastQueryFn(fetchApi, params);
    void queryClient.prefetchQuery({
        queryKey,
        queryFn,
        staleTime: CACHE_FRESH_TIME,
        gcTime: CACHE_RETENTION_TIME,
    });
};
export const useWeekForecast = (fetchApi, params, signal, enabled = true) => {
    const queryKey = getWeekForecastQueryKey(params);
    const { data, refetch, isLoading } = useForecastQuery({
        queryKey,
        enabled: enabled && areDriverParamsValid(params.driverName, params.driverSubType),
        queryFn: getWeekForecastQueryFn(fetchApi, params, signal),
    });
    return {
        data,
        isLoading,
        refetch,
    };
};
const getYearForecastQueryKey = (params) => {
    const { unitId, fiscalYear, driverName, driverSubType } = params;
    return [
        "year-forecast",
        unitId,
        fiscalYear,
        driverName,
        driverSubType,
    ];
};
const getYearForecastQueryFn = (fetchApi, params, signal) => {
    return async () => {
        const { unitId, fiscalYear, driverName, driverSubType } = params;
        const queryParams = new URLSearchParams();
        queryParams.append("fiscalYear", fiscalYear.toString());
        if (driverName !== undefined && driverName !== "") {
            queryParams.append("driverName", driverName);
        }
        if (driverSubType !== undefined && driverSubType !== "") {
            queryParams.append("driverSubType", driverSubType);
        }
        try {
            const res = await fetchApi.jsonInJsonOut(`/forecast/unit/${unitId}/year?${queryParams.toString()}`, {
                signal,
            });
            return res?.payload;
        }
        catch (error) {
            console.error("Error fetching year forecast: %o", error);
            return undefined;
        }
    };
};
export const prefetchYearForecast = (queryClient, fetchApi, params) => {
    const queryKey = getYearForecastQueryKey(params);
    const queryFn = getYearForecastQueryFn(fetchApi, params);
    void queryClient.prefetchQuery({
        queryKey,
        queryFn,
        staleTime: CACHE_FRESH_TIME,
        gcTime: CACHE_RETENTION_TIME,
    });
};
export const useYearForecast = (fetchApi, params, signal, enabled = true) => {
    const queryKey = getYearForecastQueryKey(params);
    const { data, refetch, isLoading } = useForecastQuery({
        queryKey,
        enabled: enabled && areDriverParamsValid(params.driverName, params.driverSubType),
        queryFn: getYearForecastQueryFn(fetchApi, params, signal),
    });
    return {
        data,
        isLoading,
        refetch,
    };
};
const patchForecast = async (fetchApi, path, body) => {
    await fetchApi.jsonInVoidOut(path, {
        method: "PATCH",
        body: JSON.stringify(body),
    });
};
const VIEW_CONFIG = {
    day: {
        path: (id) => `/forecast/15min/${encodeURIComponent(id)}`,
        queryKey: "day-forecast",
    },
    week: {
        path: (id) => `/forecast/day/${encodeURIComponent(id)}`,
        queryKey: "week-forecast",
    },
    year: {
        path: (id) => `/forecast/week/${encodeURIComponent(id)}`,
        queryKey: "year-forecast",
    },
};
export const usePatchForecast = (fetchApi, options) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ view, slotId, body }) => {
            if (!fetchApi)
                return;
            const { path } = VIEW_CONFIG[view];
            await patchForecast(fetchApi, path(slotId), body);
        },
        onSuccess: (_data, { view }) => {
            options?.onForecastPatched?.();
            queryClient.invalidateQueries({
                queryKey: [VIEW_CONFIG[view].queryKey],
            });
            queryClient.invalidateQueries({ queryKey: ["forecast-changes"] });
        },
        onError: (error) => {
            console.error("Error patching forecast: %o", error);
        },
    });
};
const BULK_PATCH_CONCURRENCY = 1;
const runWithConcurrency = async (tasks, concurrency) => {
    const results = new Array(tasks.length);
    let nextIndex = 0;
    const worker = async () => {
        for (;;) {
            const currentIndex = nextIndex;
            nextIndex += 1;
            if (currentIndex >= tasks.length)
                return;
            const task = tasks[currentIndex];
            if (task === undefined)
                return;
            try {
                const value = await task();
                results[currentIndex] = { status: "fulfilled", value };
            }
            catch (error) {
                results[currentIndex] = { status: "rejected", reason: error };
            }
        }
    };
    const workerCount = Math.max(1, Math.min(concurrency, tasks.length));
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
    return results;
};
export const usePatchForecastBulk = (fetchApi, options) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ view, items }) => {
            if (!fetchApi || items.length === 0)
                return [];
            const { path } = VIEW_CONFIG[view];
            const tasks = items.map(({ slotId, value }) => () => patchForecast(fetchApi, path(slotId), {
                adjustedForecast: value,
            }));
            const settled = await runWithConcurrency(tasks, BULK_PATCH_CONCURRENCY);
            return settled.map((result, index) => {
                const item = items[index];
                const slotId = item?.slotId ?? "";
                if (result.status === "fulfilled") {
                    return { slotId, status: "fulfilled" };
                }
                return { slotId, status: "rejected", error: result.reason };
            });
        },
        onSuccess: (results, { view }) => {
            const hasAnyFulfilled = results.some((r) => r.status === "fulfilled");
            if (!hasAnyFulfilled)
                return;
            options?.onForecastPatched?.();
            queryClient.invalidateQueries({
                queryKey: [VIEW_CONFIG[view].queryKey],
            });
            queryClient.invalidateQueries({ queryKey: ["forecast-changes"] });
        },
        onError: (error) => {
            console.error("Error bulk-patching forecast: %o", error);
        },
    });
};
export const downloadForecast = async (fetchApi, params) => {
    const { unitId, startDate, endDate, view, driverIds } = params;
    const queryParams = new URLSearchParams({ startDate, endDate, view });
    if (driverIds !== undefined && driverIds.length > 0) {
        queryParams.append("driverIds", driverIds.join(","));
    }
    const { blob, contentType } = await fetchApi.jsonInBlobOut(`/forecast/unit/${unitId}/download?${queryParams.toString()}`, { method: "GET" });
    if (contentType != null &&
        !contentType.includes("spreadsheetml") &&
        !contentType.includes("octet-stream")) {
        console.warn(`Unexpected response format: ${contentType}`);
    }
    return blob;
};
export const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
};
//# sourceMappingURL=forecastApi.js.map